// views/appcopy/phone.tsx — the live phone render inside App Copy.
//
// Every string on the phone is an <Ed>: click it and the panel jumps to that
// field; focus the field and it highlights here. That two-way link is the whole
// point of the screen — you edit wording against the place it lands, not a table
// of keys.
//
// It is NOT a mirror of the app's code (that lives in the tracker repo). It's a
// faithful-enough 390×844 render, driven by the declarative layouts in
// screens.ts, so a new screen is a few lines of blocks rather than a component.
// Anything the layouts don't cover is still fully editable in the panel — the
// preview is an aid, never a gate.

import React from "react";
import { OSWALD, FIGTREE } from "../../theme";
import type { Block, Chrome } from "./screens";

// The app's own palette. NOT the dashboard's tokens — this box is a picture of the
// phone, so it uses the phone's colours. Values copied from the tracker repo's
// mobile/src/lib/theme.ts (Colors + Hero) on 2026-08-23; a synced manifest replaces
// them wholesale, which is why every component reads them from context.
export type AppTheme = typeof APP;

export const APP = {
  // hero field for the detox on screen (overridden per detox)
  jar: "#f2c528", light: "#f9dc63", deep: "#dda205", onHero: "#3a2c00",
  // app surfaces + ink
  cream: "#faf3e8", white: "#ffffff", black: "#000000", fg2: "#2a2a2a", muted: "#5a5a5a",
  line: "rgba(0,0,0,0.12)",
  // washes + status
  mint: "#d8ecd1", pink: "#fbe9e8", peach: "#ffe8d8", sky: "#dcf1f7",
  orange: "#f47f49", purple: "#3c3291", binderPurple: "#6f63c4",
  green: "#08a225", coral: "#ff5b4a", charcoal: "#3a3a3c",
};

const SEL = "#f47f49"; // selection outline — the brand orange, on a light surface

// Every colour the phone draws with comes from here, so a manifest pushed by the
// app (or simply picking a different detox) restyles the whole preview.
export const PaletteCtx = React.createContext<AppTheme>(APP);

// Stand-ins for what the app fills in at runtime, so the preview reads like a
// real screen instead of a sentence full of braces.
export const VARS: Record<string, string> = {
  DAY: "3", TOTAL: "14", DAYS: "14", DETOX: "Liver Cleanse", ITEM: "Liver Cleanse",
  CURRENT: "Liver Cleanse", DATE: "Aug 26", TIME: "7:30 AM", H: "1", M: "42",
  N: "2", COUNT: "4", LENGTH: "30 sec", EMAIL: " (dustin@boostblenz.com)",
  BINDER: " · binder day", WHAT: "Detox and binder both logged. ",
  AFTER: " after your Liver Cleanse",
};

export const resolveVars = (s: string, vars: Record<string, string> = VARS) =>
  String(s ?? "").replace(/\{([A-Za-z0-9_]+)\}/g, (m, n) => (vars[n] != null ? vars[n] : m));

export type PhoneCtx = {
  value: (key: string) => string;      // the working text for a key
  has: (key: string) => boolean;       // is this key in the data at all?
  sel: string | null;
  setSel: (k: string | null) => void;
  markers: boolean;
  vars: Record<string, string>;        // {DETOX}, {DAY}… for the detox being previewed
  keyVars?: Record<string, Record<string, string>>; // per-key overrides — see KEY_VARS
  theme: AppTheme;
  hero: string;                        // the product shot on the Today canvas
  product: string;                     // the detox name the app would show
};

// ---------------------------------------------------------------- one string
export function Ed({ ctx, k, style = {}, caps, block, fallback }: {
  ctx: PhoneCtx; k: string; style?: React.CSSProperties; caps?: boolean; block?: boolean; fallback?: string;
}) {
  const [hov, setHov] = React.useState(false);
  const known = ctx.has(k);
  // A layout referencing a key the data doesn't have renders its fallback (or
  // nothing) instead of breaking the screen — the same "built-in is the floor"
  // rule the app itself follows.
  if (!known) {
    return fallback ? <span style={{ ...style, textTransform: caps ? "uppercase" : "none", display: block ? "block" : "inline-block" }}>{fallback}</span> : null;
  }
  // An empty value is a DELETED line: the app renders nothing and collapses the row,
  // so the preview does too. With markers on it leaves a dashed stub, otherwise a
  // removed line would be unreachable in the preview.
  const val = ctx.value(k);
  if (!val.trim()) {
    if (!ctx.markers) return null;
    return (
      <span
        title={`${k} — removed`}
        onClick={(e) => { e.stopPropagation(); ctx.setSel(k); }}
        style={{
          display: block ? "block" : "inline-block", cursor: "text", borderRadius: 3,
          border: `1px dashed ${SEL}`, opacity: 0.65,
          fontFamily: FIGTREE, fontSize: 9, letterSpacing: "0.06em", textTransform: "uppercase",
          color: SEL, padding: "0 4px", lineHeight: 1.6,
        }}
      >removed</span>
    );
  }
  const on = ctx.sel === k;
  return (
    <span
      title={k}
      onClick={(e) => { e.stopPropagation(); ctx.setSel(k); }}
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={{
        position: "relative", display: block ? "block" : "inline-block", cursor: "text", borderRadius: 3,
        outline: on ? `1.5px dashed ${SEL}` : hov ? "1.5px dashed rgba(0,0,0,0.4)" : "1.5px dashed transparent",
        outlineOffset: 2,
        background: on ? "rgba(244,127,73,0.18)" : "transparent",
        transition: "background 140ms ease, outline-color 140ms ease",
        textTransform: caps ? "uppercase" : "none",
        whiteSpace: "pre-wrap",
        ...style,
      }}
    >
      {resolveVars(val, ctx.keyVars?.[k] ? { ...ctx.vars, ...ctx.keyVars[k] } : ctx.vars)}
      {ctx.markers && <span style={{ position: "absolute", top: -3, right: -3, width: 6, height: 6, borderRadius: 999, background: SEL }} />}
    </span>
  );
}

// ---------------------------------------------------------------- blocks
function Btn({ ctx, k, tone = "yellow" }: { ctx: PhoneCtx; k: string; tone?: string }) {
  const A = React.useContext(PaletteCtx);
  const bg = tone === "dark" ? A.black : tone === "ghost" ? A.white : tone === "red" ? "#c0392b" : tone === "purple" ? A.binderPurple : A.jar;
  const fg = tone === "yellow" ? A.black : tone === "ghost" ? A.fg2 : "#fff";
  return (
    <div style={{
      width: "100%", minHeight: 50, borderRadius: 999, background: bg,
      border: tone === "ghost" ? "1.5px solid rgba(0,0,0,0.16)" : "none",
      display: "flex", alignItems: "center", justifyContent: "center", padding: "10px 16px", boxSizing: "border-box",
    }}>
      <Ed ctx={ctx} k={k} caps style={{ fontFamily: FIGTREE, fontWeight: 800, fontSize: 15, letterSpacing: "0.04em", color: fg, textAlign: "center" }} />
    </div>
  );
}

function Bullet({ ctx, k }: { ctx: PhoneCtx; k: string }) {
  const A = React.useContext(PaletteCtx);
  return (
    <div style={{ display: "flex", gap: 9, alignItems: "flex-start" }}>
      <span style={{ width: 6, height: 6, borderRadius: 999, background: A.jar, marginTop: 7, flexShrink: 0 }} />
      <Ed ctx={ctx} k={k} style={{ fontFamily: FIGTREE, fontSize: 13.5, lineHeight: 1.45, color: A.fg2 }} />
    </div>
  );
}

export function Blocks({ ctx, blocks }: { ctx: PhoneCtx; blocks: Block[] }) {
  const A = React.useContext(PaletteCtx);
  return (
    <>
      {blocks.map((b, i) => {
        switch (b.t) {
          case "eyebrow":
            return <Ed key={i} ctx={ctx} k={b.k} caps block style={{ fontFamily: FIGTREE, fontWeight: 700, fontSize: 11, letterSpacing: "0.1em", color: "rgba(0,0,0,0.55)" }} />;
          case "title":
            return <Ed key={i} ctx={ctx} k={b.k} caps block style={{ fontFamily: OSWALD, fontWeight: 700, fontSize: b.small ? 21 : 26, letterSpacing: "0.02em", lineHeight: 1.08, color: A.black }} />;
          case "lead":
            return <Ed key={i} ctx={ctx} k={b.k} block style={{ fontFamily: FIGTREE, fontSize: 13.5, lineHeight: 1.5, color: A.muted }} />;
          case "note":
            return (
              <div key={i} style={{ background: "rgba(0,0,0,0.04)", borderRadius: 12, padding: "11px 13px" }}>
                <Ed ctx={ctx} k={b.k} block style={{ fontFamily: FIGTREE, fontSize: 12.5, lineHeight: 1.45, color: A.muted }} />
              </div>
            );
          case "small":
            return <Ed key={i} ctx={ctx} k={b.k} block style={{ fontFamily: FIGTREE, fontSize: 11.5, lineHeight: 1.45, color: "rgba(0,0,0,0.45)", textAlign: b.center ? "center" : "left" }} />;
          case "section":
            return <Ed key={i} ctx={ctx} k={b.k} caps block style={{ fontFamily: FIGTREE, fontWeight: 700, fontSize: 11, letterSpacing: "0.1em", color: "rgba(0,0,0,0.42)", marginTop: 4 }} />;
          case "bullets":
            return <div key={i} style={{ display: "grid", gap: 7 }}>{b.ks.map((k) => <Bullet key={k} ctx={ctx} k={k} />)}</div>;
          case "field":
            return (
              <div key={i} style={{ minHeight: 48, borderRadius: 12, border: "1.5px solid rgba(0,0,0,0.16)", background: A.white, display: "flex", alignItems: "center", padding: "0 14px" }}>
                <Ed ctx={ctx} k={b.k} style={{ fontFamily: FIGTREE, fontSize: 14.5, color: "rgba(0,0,0,0.38)" }} />
              </div>
            );
          case "btn":
            return <Btn key={i} ctx={ctx} k={b.k} tone={b.tone} />;
          case "link":
            return <Ed key={i} ctx={ctx} k={b.k} block style={{ fontFamily: FIGTREE, fontWeight: 700, fontSize: 13.5, color: A.fg2, textDecoration: "underline", textAlign: "center" }} />;
          case "error":
            return <Ed key={i} ctx={ctx} k={b.k} block style={{ fontFamily: FIGTREE, fontSize: 12.5, lineHeight: 1.45, color: "#c0392b" }} />;
          case "chips":
            return (
              <div key={i} style={{ display: "flex", flexWrap: "wrap", gap: 7 }}>
                {b.ks.map((k, n) => (
                  <span key={k} style={{ borderRadius: 999, padding: "8px 13px", background: n === 1 ? A.jar : "rgba(0,0,0,0.05)", border: "1px solid " + (n === 1 ? A.deep : "transparent") }}>
                    <Ed ctx={ctx} k={k} style={{ fontFamily: FIGTREE, fontWeight: 700, fontSize: 12.5, color: A.black }} />
                  </span>
                ))}
              </div>
            );
          case "options":
            return (
              <div key={i} style={{ display: "grid", gap: 8 }}>
                {b.items.map((o, n) => (
                  <div key={o.title} style={{ border: "1.5px solid " + (n === 0 ? A.deep : A.line), background: n === 0 ? "rgba(242,197,40,0.18)" : A.white, borderRadius: 13, padding: "11px 13px" }}>
                    <Ed ctx={ctx} k={o.title} block style={{ fontFamily: FIGTREE, fontWeight: 700, fontSize: 14, color: A.black }} />
                    {o.sub && <Ed ctx={ctx} k={o.sub} block style={{ fontFamily: FIGTREE, fontSize: 12, color: A.muted, marginTop: 2 }} />}
                  </div>
                ))}
              </div>
            );
          case "rows":
            return (
              <div key={i} style={{ background: A.white, borderRadius: 14, padding: "2px 13px", border: "1px solid " + A.line }}>
                {b.items.map((r, n) => (
                  <div key={r.label} style={{ display: "flex", alignItems: "center", gap: 10, padding: "13px 0", borderTop: n ? "1px solid " + A.line : "none" }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <Ed ctx={ctx} k={r.label} block style={{ fontFamily: FIGTREE, fontWeight: 600, fontSize: 14, color: A.black }} />
                      {r.sub && <Ed ctx={ctx} k={r.sub} block style={{ fontFamily: FIGTREE, fontSize: 11.5, color: A.muted, marginTop: 2 }} />}
                    </div>
                    {r.value
                      ? <Ed ctx={ctx} k={r.value} style={{ fontFamily: FIGTREE, fontWeight: 700, fontSize: 12.5, color: A.muted, flexShrink: 0 }} />
                      : <span style={{ color: "rgba(0,0,0,0.3)", fontSize: 15, flexShrink: 0 }}>›</span>}
                  </div>
                ))}
              </div>
            );
          case "checks":
            return (
              <div key={i} style={{ display: "grid", gap: 8 }}>
                {b.items.map((r) => (
                  <div key={r.label} style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    <span style={{ width: 19, height: 19, borderRadius: 999, border: "1.8px solid " + A.deep, background: r.done ? A.jar : "transparent", flexShrink: 0 }} />
                    <Ed ctx={ctx} k={r.label} style={{ flex: 1, fontFamily: FIGTREE, fontWeight: 600, fontSize: 13.5, color: A.black }} />
                    {r.value && <Ed ctx={ctx} k={r.value} style={{ fontFamily: FIGTREE, fontSize: 12, color: A.muted }} />}
                  </div>
                ))}
              </div>
            );
          case "video":
            return (
              <div key={i} style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 9, minHeight: 44, padding: "0 16px", borderRadius: 999, background: A.white, border: "1.5px solid rgba(0,0,0,0.15)" }}>
                <span style={{ width: 22, height: 22, borderRadius: 999, background: "rgba(0,0,0,0.07)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                  <svg width="11" height="11" viewBox="0 0 24 24"><path d="M7 4.5l13 7.5-13 7.5z" fill={A.fg2} /></svg>
                </span>
                <Ed ctx={ctx} k={b.k} style={{ fontFamily: FIGTREE, fontWeight: 700, fontSize: 13, color: A.fg2 }} />
                {b.meta && <Ed ctx={ctx} k={b.meta} style={{ fontFamily: FIGTREE, fontWeight: 600, fontSize: 11.5, color: A.fg2, opacity: 0.6 }} />}
              </div>
            );
          case "pill":
            return (
              <span key={i} style={{ alignSelf: "flex-start", background: A.pink, borderRadius: 6, padding: "4px 8px" }}>
                <Ed ctx={ctx} k={b.k} caps style={{ fontFamily: FIGTREE, fontWeight: 800, fontSize: 9.5, letterSpacing: "0.09em", color: "#c0392b" }} />
              </span>
            );
          case "banner":
            return (
              <div key={i} style={{ background: A.mint, borderRadius: 16, padding: "14px 15px", textAlign: "center" }}>
                <Ed ctx={ctx} k={b.k} caps block style={{ fontFamily: OSWALD, fontWeight: 700, fontSize: 21, letterSpacing: "0.02em", color: A.black, lineHeight: 1.1 }} />
                {b.sub && <Ed ctx={ctx} k={b.sub} block style={{ fontFamily: FIGTREE, fontSize: 12.5, color: A.muted, marginTop: 4 }} />}
              </div>
            );
          case "steps":
            return (
              <div key={i} style={{ display: "grid", gap: 8 }}>
                {b.items.map((s, n) => (
                  <div key={s.title} style={{ display: "flex", gap: 11, alignItems: "flex-start", border: "1px solid " + A.line, background: A.white, borderRadius: 13, padding: "11px 12px" }}>
                    <span style={{ width: 25, height: 25, borderRadius: 999, flexShrink: 0, background: "rgba(0,0,0,0.06)", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: FIGTREE, fontWeight: 700, fontSize: 12.5, color: A.black }}>{n + 1}</span>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <Ed ctx={ctx} k={s.title} block style={{ fontFamily: FIGTREE, fontWeight: 700, fontSize: 14, color: A.black }} />
                      {s.sub && <Ed ctx={ctx} k={s.sub} block style={{ fontFamily: FIGTREE, fontSize: 12.5, lineHeight: 1.4, color: A.muted, marginTop: 2 }} />}
                    </div>
                  </div>
                ))}
              </div>
            );
          case "stats":
            return (
              <div key={i} style={{ display: "flex", gap: 10 }}>
                {b.ks.map((k, n) => (
                  <div key={k} style={{ flex: 1, background: A.purple, borderRadius: 16, padding: "13px 14px" }}>
                    <div style={{ fontFamily: OSWALD, fontWeight: 700, fontSize: 24, color: "#fff", lineHeight: 1 }}>{n === 0 ? "86%" : "6"}</div>
                    <Ed ctx={ctx} k={k} block style={{ fontFamily: FIGTREE, fontWeight: 600, fontSize: 11.5, color: "rgba(255,255,255,0.78)", marginTop: 3 }} />
                  </div>
                ))}
              </div>
            );
          case "oneof":
            // The app shows exactly ONE of these; the preview can't know which, so it
            // says so rather than stacking them and looking like a rendering bug.
            return (
              <div key={i} style={{ border: "1px dashed rgba(0,0,0,0.22)", borderRadius: 10, padding: "7px 9px 8px", display: "grid", gap: 5 }}>
                <div style={{ fontFamily: FIGTREE, fontWeight: 700, fontSize: 8.5, letterSpacing: "0.1em", textTransform: "uppercase", color: "rgba(0,0,0,0.38)" }}>
                  One of these{b.note ? ` — ${b.note}` : ""}
                </div>
                {b.ks.map((k) => (
                  <Ed key={k} ctx={ctx} k={k} block style={{ fontFamily: FIGTREE, fontSize: 12.5, lineHeight: 1.45, color: A.fg2 }} />
                ))}
              </div>
            );
          case "a11y":
            /**
             * A screen-reader label. It is NOT on the phone — it is what VoiceOver says out
             * loud for a control whose meaning is a picture. Drawn as a quiet strip with a
             * speaker so it can never be mistaken for text a sighted customer sees, and so
             * the wording is somewhere Dustin can find and change. These lived as literals
             * in the app source until 2026-09-10, heard by every blind customer and editable
             * by nobody.
             */
            return (
              <div key={i} style={{ display: "grid", gap: 4, border: "1px dashed rgba(0,0,0,0.16)", borderRadius: 10, padding: "6px 9px 7px", background: "rgba(0,0,0,0.02)" }}>
                <div style={{ fontFamily: FIGTREE, fontWeight: 700, fontSize: 8.5, letterSpacing: "0.1em", textTransform: "uppercase", color: "rgba(0,0,0,0.34)" }}>
                  🔊 Read aloud — not shown on screen
                </div>
                {[...(b.k ? [b.k] : []), ...(b.ks ?? [])].map((k) => (
                  <Ed key={k} ctx={ctx} k={k} block style={{ fontFamily: FIGTREE, fontSize: 11.5, fontStyle: "italic", color: "rgba(0,0,0,0.5)" }} />
                ))}
              </div>
            );
          case "gap":
            return <div key={i} style={{ height: b.h ?? 6 }} />;
          default:
            return null;
        }
      })}
    </>
  );
}

// ---------------------------------------------------------------- tab bar
export function TabBar({ ctx, highlight }: { ctx: PhoneCtx; highlight?: boolean }) {
  const A = React.useContext(PaletteCtx);
  const cell = (k: string, icon: React.ReactNode, active?: boolean) => (
    <div key={k} style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", gap: 4 }}>
      {icon}
      <Ed ctx={ctx} k={k} style={{ fontFamily: FIGTREE, fontWeight: active ? 700 : 500, fontSize: 10.5, color: active ? SEL : A.muted }} />
    </div>
  );
  const st = { stroke: A.muted, strokeWidth: 1.9, fill: "none", strokeLinecap: "round" as const };
  return (
    <div style={{
      position: "absolute", left: 0, right: 0, bottom: 0, height: 72, zIndex: 20,
      background: "#fbfbfc", borderTop: "1px solid rgba(0,0,0,0.07)",
      boxShadow: highlight ? `0 0 0 2px ${SEL} inset` : "none",
      display: "flex", alignItems: "flex-start", paddingTop: 10,
    }}>
      {cell("tab.today", <svg width="21" height="21" viewBox="0 0 24 24"><circle cx="12" cy="12" r="4" stroke={SEL} strokeWidth="1.9" fill="none" /><g stroke={SEL} strokeWidth="1.9" strokeLinecap="round"><path d="M12 3v2.4M12 18.6V21M3 12h2.4M18.6 12H21M5.6 5.6l1.7 1.7M16.7 16.7l1.7 1.7M18.4 5.6l-1.7 1.7M7.3 16.7l-1.7 1.7" /></g></svg>, true)}
      {cell("tab.progress", <svg width="21" height="21" viewBox="0 0 24 24"><g {...st}><path d="M6 15v5M12 8v12M18 12v8" /></g></svg>)}
      {cell("tab.help", <svg width="21" height="21" viewBox="0 0 24 24"><circle cx="12" cy="12" r="9" {...st} /><path d="M9.6 9.4a2.5 2.5 0 114 2c-.9.7-1.6 1.1-1.6 2.3M12 17.2h0" {...st} /></svg>)}
      {cell("tab.more", <svg width="21" height="21" viewBox="0 0 24 24"><g {...st}><path d="M4 7h16M4 12h16M4 17h16" /></g></svg>)}
    </div>
  );
}

// ---------------------------------------------------------------- chromes
// The yellow Today canvas: gradient, jar, date + product pill. Shared by the
// Today dock and (blurred) behind the tracker sheet.
function JarCanvas({ ctx, dim }: { ctx: PhoneCtx; dim?: boolean }) {
  const A = React.useContext(PaletteCtx);
  return (
    <div style={{ position: "absolute", inset: 0, filter: dim ? "blur(2.5px)" : "none" }}>
      <div style={{
        position: "absolute", inset: 0,
        backgroundImage: `linear-gradient(102deg, rgba(255,255,255,0.13), rgba(255,255,255,0) 44%, rgba(0,0,0,0.07)), linear-gradient(180deg, ${A.light} 0%, ${A.jar} 46%, ${A.deep} 100%)`,
      }} />
      <img src={ctx.hero} alt="" style={{ position: "absolute", left: "50%", top: 300, width: "74%", transform: "translate(-50%,-50%)", filter: "drop-shadow(0 26px 34px rgba(0,0,0,0.22))" }} />
      <div style={{ position: "absolute", left: 0, right: 0, top: 0, padding: "52px 20px 0" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 11 }}>
          <div style={{ fontFamily: OSWALD, fontWeight: 700, fontSize: 28, letterSpacing: "0.02em", textTransform: "uppercase", color: A.black, lineHeight: 1.05 }}>Aug 20</div>
          <div style={{ display: "flex", alignItems: "center", gap: 8, background: "#fff", borderRadius: 999, padding: "8px 12px", minHeight: 38 }}>
            <span style={{ width: 12, height: 12, borderRadius: "50%", background: A.jar, flexShrink: 0 }} />
            <span style={{ fontFamily: FIGTREE, fontWeight: 700, fontSize: 14, color: A.black }}>{ctx.product.split(" ")[0]}</span>
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" style={{ opacity: 0.5 }}><path d="M6 9.5l6 6 6-6" stroke={A.black} strokeWidth="2.2" strokeLinecap="round" /></svg>
          </div>
        </div>
      </div>
    </div>
  );
}

export function PhoneRender({ ctx, chrome, blocks, tabHighlight }: {
  ctx: PhoneCtx; chrome: Chrome; blocks: Block[]; tabHighlight?: boolean;
}) {
  const A = React.useContext(PaletteCtx);
  const stack = (style: React.CSSProperties) => (
    <div style={{ display: "flex", flexDirection: "column", gap: 11, ...style }}>
      <Blocks ctx={ctx} blocks={blocks} />
    </div>
  );

  if (chrome === "today") {
    return (
      <>
        <JarCanvas ctx={ctx} />
        <div style={{
          position: "absolute", left: 0, right: 0, bottom: 72, zIndex: 10, background: "#fff",
          borderRadius: "28px 28px 0 0", boxShadow: "0 -10px 34px rgba(0,0,0,0.18)", padding: "12px 16px 18px",
          // 🔴 SCROLLS, never clips (same rule as the page chrome below). A 430px cap with
          // `overflow: hidden` cut off everything under "Shop another kit" on Program complete —
          // a line you cannot see is a line you cannot click to edit.
          maxHeight: 640, overflowY: "auto", overflowX: "hidden",
        }}>
          <span style={{ display: "block", width: 42, height: 5, borderRadius: 999, background: "rgba(0,0,0,0.16)", margin: "0 auto 12px" }} />
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 11 }}>
            <span style={{ width: 11, height: 11, borderRadius: "50%", background: A.jar, flexShrink: 0 }} />
            <div style={{ fontFamily: OSWALD, fontWeight: 700, fontSize: 21, letterSpacing: "0.02em", textTransform: "uppercase", color: A.black, lineHeight: 1.05 }}>{ctx.product}</div>
          </div>
          {stack({})}
        </div>
        <TabBar ctx={ctx} highlight={tabHighlight} />
      </>
    );
  }

  if (chrome === "sheet") {
    return (
      <>
        <JarCanvas ctx={ctx} dim />
        <div style={{ position: "absolute", inset: 0, background: "rgba(0,0,0,0.42)", zIndex: 14 }} />
        <div style={{
          position: "absolute", left: 0, right: 0, bottom: 72, top: 96, zIndex: 16, background: "#fff",
          borderRadius: "26px 26px 0 0", boxShadow: "0 -16px 44px rgba(0,0,0,0.32)", padding: "16px 16px 18px",
          overflowY: "auto", overflowX: "hidden", display: "flex", flexDirection: "column",
        }}>
          {stack({ flexShrink: 0 })}
        </div>
        <TabBar ctx={ctx} highlight={tabHighlight} />
      </>
    );
  }

  if (chrome === "modal") {
    return (
      <>
        <JarCanvas ctx={ctx} dim />
        <div style={{ position: "absolute", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 14 }} />
        <div style={{
          position: "absolute", left: 16, right: 16, top: "50%", transform: "translateY(-50%)", zIndex: 16,
          background: "#fff", borderRadius: 24, padding: "20px 18px", maxHeight: 640, overflowY: "auto", overflowX: "hidden",
          boxShadow: "0 24px 60px rgba(0,0,0,0.4)",
        }}>
          {stack({})}
        </div>
        <TabBar ctx={ctx} highlight={tabHighlight} />
      </>
    );
  }

  if (chrome === "notification") {
    return (
      <div style={{ position: "absolute", inset: 0, background: "linear-gradient(180deg,#2b2450,#141029)" }}>
        <div style={{ padding: "84px 14px 0", display: "flex", flexDirection: "column", gap: 10 }}>
          <div style={{ fontFamily: OSWALD, fontWeight: 700, fontSize: 52, color: "rgba(255,255,255,0.92)", textAlign: "center", lineHeight: 1 }}>7:30</div>
          <div style={{ fontFamily: FIGTREE, fontSize: 13, color: "rgba(255,255,255,0.6)", textAlign: "center", marginBottom: 12 }}>Thursday, August 20</div>
          <div style={{ background: "rgba(255,255,255,0.94)", borderRadius: 18, padding: "12px 13px", display: "flex", flexDirection: "column", gap: 5 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 7 }}>
              <span style={{ width: 15, height: 15, borderRadius: 4, background: A.jar, flexShrink: 0 }} />
              <span style={{ fontFamily: FIGTREE, fontWeight: 600, fontSize: 10.5, letterSpacing: "0.06em", textTransform: "uppercase", color: A.muted }}>Detox Tracker</span>
              <span style={{ marginLeft: "auto", fontFamily: FIGTREE, fontSize: 10.5, color: A.muted }}>now</span>
            </div>
            {stack({ gap: 5 })}
          </div>
        </div>
      </div>
    );
  }

  // "page" (cream page with the tab bar) and "plain" (full-bleed, no tab bar)
  const page = chrome === "page";
  return (
    <div style={{ position: "absolute", inset: 0, background: A.cream, overflow: "hidden" }}>
      {/* 🔴 SCROLLS, never clips. This was `overflow: hidden` with a hard maxHeight, so a drawn
          screen with more blocks than fit had its last lines cut off the bottom with nothing to
          say so — copy Dustin could edit on the right but could not find on the left, which is
          exactly the complaint he kept raising (2026-09-09). */}
      <div style={{ padding: page ? "58px 18px 0" : "70px 20px 0", display: "flex", flexDirection: "column", gap: 11, maxHeight: page ? 760 : 820, overflowY: "auto", overflowX: "hidden" }}>
        <Blocks ctx={ctx} blocks={blocks} />
      </div>
      {page && <TabBar ctx={ctx} highlight={tabHighlight} />}
    </div>
  );
}

// The device shell. Fixed 390×844 and scaled — never reflowed, so what you see is
// a real phone's proportions, not a responsive web layout.
export function Phone({ ctx, chrome, blocks, scale, tabHighlight }: {
  ctx: PhoneCtx; chrome: Chrome; blocks: Block[]; scale: number; tabHighlight?: boolean;
}) {
  return (
    <PaletteCtx.Provider value={ctx.theme}>
    <div style={{ width: 390 * scale, height: 844 * scale, flexShrink: 0 }}>
      <div
        onClick={() => ctx.setSel(null)}
        style={{
          width: 390, height: 844, transform: `scale(${scale})`, transformOrigin: "top left",
          position: "relative", borderRadius: 46, overflow: "hidden", background: "#fff",
          boxShadow: "0 30px 70px rgba(0,0,0,0.45)",
        }}
      >
        <PhoneRender ctx={ctx} chrome={chrome} blocks={blocks} tabHighlight={tabHighlight} />
      </div>
    </div>
    </PaletteCtx.Provider>
  );
}
